import { WarningIcon } from "@images/svgIcons/warningIcon"
import { useCurrentBox } from "@modules/box/useBox"
import { BoxLeadDraftEntity } from "@modules/boxLead/entities/boxLeadInput"
import { ExperienceLevel, Goals, HeardAboutUs } from "@modules/boxLead/entities/boxLeadTypes"
import { BoxLeadInputKey } from "@modules/boxLead/formik/boxLeadFormik"
import { word } from "@modules/core/utils/i18n"
import { SearchUserInput } from "@modules/formik/components/searchUserInput"
import { User } from "@modules/profile/profileTypes"
import { FormikInput } from "@modules/ui/components/formikInput"
import { HUButton } from "@modules/ui/components/huButton"
import { HUText } from "@modules/ui/components/huText"
import { useConfirmPopup } from "@modules/ui/components/popup/huConfirmPopup"
import { displayedDateFormat } from "@modules/utils/dateUtils"
import { SimpleFormik } from "@src/typings/formik"
import dayjs from "dayjs"
import { Check, X } from "lucide-react"
import { useEffect, useState } from "react"
import { useTheme } from "styled-components"

export type BoxLeadFormStepTwoProps = {
  handleSubmit: () => void
  onPrevious: () => void
  formik: SimpleFormik<BoxLeadDraftEntity, BoxLeadInputKey>
  isSubmitting: boolean
  isFormFieldInValid: (name: BoxLeadInputKey) => boolean
  getFormErrorMessage: (name: BoxLeadInputKey, errorMessage?: string) => React.JSX.Element | undefined
  disabled: boolean
  isEdition: boolean
  user?: User | null
}

export function BoxLeadFormStepTwo({
  handleSubmit,
  onPrevious,
  formik,
  isSubmitting,
  isFormFieldInValid,
  getFormErrorMessage,
  disabled,
  isEdition,
  user,
  className,
  ...props
}: BoxLeadFormStepTwoProps & React.ComponentProps<"div">) {
  const theme = useTheme()
  const { result: box } = useCurrentBox()
  const defaultClassName = "flex flex-column gap-3 mb-4 w-full"
  const selectedVariant = "border-2 border-purple-500"
  const neutralVariant = "border-1 border-neutralColor-200"
  const confirmPopup = useConfirmPopup()

  const [haveHustleUpAccount, setHaveHustleUpAccount] = useState(
    formik.initialValues.userId || !formik.initialValues.firstname ? true : false,
  )

  const hearAboutUsItem = [
    {
      value: HeardAboutUs.WORD_OF_MOUTH,
      label: word("boxLeadEntity.wording.hearAboutUs.wordOfMouth"),
    },
    {
      value: HeardAboutUs.SOCIAL_NETWORK,
      label: word("boxLeadEntity.wording.hearAboutUs.socialNetwork"),
    },
    {
      value: HeardAboutUs.LOCAL_ADVERTISING,
      label: word("boxLeadEntity.wording.hearAboutUs.localAdvertising"),
    },
    {
      value: HeardAboutUs.ONLINE_SEARCH,
      label: word("boxLeadEntity.wording.hearAboutUs.onlineSearch"),
    },
    {
      value: HeardAboutUs.SPORTS_EVENTS_COMPETITIONS,
      label: word("boxLeadEntity.wording.hearAboutUs.events"),
    },
    {
      value: HeardAboutUs.PASSED_BY,
      label: word("boxLeadEntity.wording.hearAboutUs.passedBy"),
    },
    {
      value: HeardAboutUs.INFLUENCERS_AMBASSADORS,
      label: word("boxLeadEntity.wording.hearAboutUs.influencers"),
    },
    {
      value: HeardAboutUs.OTHER,
      label: word("boxLeadEntity.wording.hearAboutUs.other"),
    },
  ]

  const goalsItem = [
    {
      value: Goals.LOSE_WEIGHT,
      label: word("boxLeadEntity.wording.goals.loseWeight"),
    },
    {
      value: Goals.GETTING_BACK_INTO_SPORT,
      label: word("boxLeadEntity.wording.goals.gettingBackIntoSport"),
    },
    {
      value: Goals.RETURN_TO_EXERCISE_AFTER_AN_INJURY,
      label: word("boxLeadEntity.wording.goals.returnToExerciseAfterAnInjury"),
    },
    {
      value: Goals.PERFORMANCE,
      label: word("boxLeadEntity.wording.goals.performance"),
    },
    {
      value: Goals.OTHER,
      label: word("boxLeadEntity.wording.goals.other"),
    },
  ]

  const experienceLevelItem = [
    {
      value: ExperienceLevel.LESS_THAN_SIX_MONTHS,
      label: word("boxLeadEntity.wording.experienceLevel.lessThanSixMonths"),
    },
    {
      value: ExperienceLevel.LESS_THAN_A_YEAR,
      label: word("boxLeadEntity.wording.experienceLevel.lessThanAYear"),
    },
    {
      value: ExperienceLevel.MORE_THAN_A_YEAR,
      label: word("boxLeadEntity.wording.experienceLevel.moreThanAYear"),
    },
    {
      value: ExperienceLevel.NONE,
      label: word("boxLeadEntity.wording.experienceLevel.none"),
    },
  ]

  const bodyEditUserPopup = (
    <div className="flex flex-column align-items-center align-content-center justify-content-center gap-3">
      <HUText fontStyle="TL" color={theme.mainWhite} className="flex">
        {word("box.lead.form.input.popup.edit.header")}
      </HUText>
      <HUText fontStyle="BM" color={theme.neutralColor700} className="flex w-22rem text-center">
        {word("box.lead.form.input.popup.edit.content")}
      </HUText>
    </div>
  )

  useEffect(() => {
    const countryCode = formik.getFieldProps("phoneNumberCountryCode").value
    if (box?.phoneNumberWithPrefix.countryCode && !countryCode) {
      formik.setFieldValue("phoneNumberCountryCode", box.phoneNumberWithPrefix.countryCode)
    }
  }, [box?.phoneNumberWithPrefix.countryCode])

  const handlePreSubmit = () => {
    if (formik.initialValues.userId && formik.initialValues.userId !== formik.values.userId) {
      confirmPopup.show({
        width: "418px",
        title: "",
        message: bodyEditUserPopup,
        accept: async () => handleSubmit(),
        footerProps: {
          align: true,
          cancelText: word("global.cancel"),
          confirmText: word("global.continue"),
          confirmClassName: "flex-1",
          cancelClassName: "flex-1",
          footerClassName: "flex flex-row justify-content-around w-full",
        },
      })
      return
    }

    handleSubmit()
  }

  return (
    <div className={className ?? defaultClassName} {...props}>
      {/* HAVE HUSTLEUP ACCOUNT */}
      <div className="flex flex-column gap-4 bg-neutralColor-50 border-round-xl p-4">
        <div className="flex flex-column gap-2">
          <HUText type="label" color={theme.inputLabelColor} fontStyle="LS">
            {word("box.lead.form.input.haveHustleUpAccount.label.header").toUpperCase()}
            <HUText fontStyle="LL" color={theme.necessaryInput}>
              {" "}
              *
            </HUText>
          </HUText>
          <div className="flex flex-rows w-full gap-2">
            <div
              className={`flex w-6 gap-2 justify-content-center align-items-center cursor-pointer border-round-2xl py-4
              ${haveHustleUpAccount ? selectedVariant : neutralVariant}`}
              onClick={() => {
                setHaveHustleUpAccount(true)
                formik.setFieldValue("firstname", "")
                formik.setFieldValue("lastname", "")
              }}
            >
              <Check color={theme.neutralColor600} size={15} />
              <HUText
                type="label"
                color={theme.neutralColor600}
                fontStyle="LL"
                className="max-w-9rem cursor-pointer"
                style={{ whiteSpace: "break-spaces", overflowWrap: "break-word" }}
              >
                {word("box.lead.form.input.haveHustleUpAccount.label.yes")}
              </HUText>
            </div>
            <div
              className={`flex w-6 gap-2 justify-content-center align-items-center cursor-pointer border-round-2xl py-4
              ${!haveHustleUpAccount ? selectedVariant : neutralVariant}`}
              onClick={() => {
                setHaveHustleUpAccount(false)
                formik.setFieldValue("userId", "")
              }}
            >
              <X color={theme.neutralColor600} size={15} />
              <HUText
                type="label"
                color={theme.neutralColor600}
                fontStyle="LL"
                className="max-w-9rem cursor-pointer"
                style={{ whiteSpace: "break-spaces", overflowWrap: "break-word" }}
              >
                {word("box.lead.form.input.haveHustleUpAccount.label.no")}
              </HUText>
            </div>
          </div>
        </div>

        {/* FIRSTNAME / LASTNAME */}
        {!haveHustleUpAccount && (
          <div className="flex flex-rows gap-2">
            <FormikInput
              label={word("global.form.label.firstName")}
              type="text"
              setFieldValue={formik.setFieldValue}
              flex
              isInvalid={isFormFieldInValid("firstname")}
              getFieldProps={formik.getFieldProps}
              name="firstname"
              error={getFormErrorMessage("firstname")}
              placeHolder={word("auth.signup.form.first_name")}
              isRequiredInput={true}
            />
            <FormikInput
              label={word("global.form.label.lastName")}
              type="text"
              setFieldValue={formik.setFieldValue}
              flex
              isInvalid={isFormFieldInValid("lastname")}
              getFieldProps={formik.getFieldProps}
              name="lastname"
              error={getFormErrorMessage("lastname")}
              placeHolder={word("auth.signup.form.last_name")}
              isRequiredInput={true}
            />
          </div>
        )}

        {/* SEARCH USER */}
        {haveHustleUpAccount && (
          <SearchUserInput
            user={user}
            classNameDiv="flex flex-column gap-3"
            textDescription={word("box.lead.form.input.haveHustleUpAccount.label.searchUser")}
            name="userId"
            isRequiredInput={false}
            isInvalid={isFormFieldInValid("userId")}
            setFieldValue={formik.setFieldValue}
            error={getFormErrorMessage("userId")}
            searchDescriptionText={word("box.lead.form.input.haveHustleUpAccount.dialog.searchUser")}
          />
        )}
      </div>
      {/* FIRST CONTACT DATE */}
      <FormikInput
        label={word("box.lead.form.input.firstContactDate.label")}
        flex
        formikStyle={{ flexGrow: 1 }}
        getFieldProps={formik.getFieldProps}
        isInvalid={isFormFieldInValid("firstContact")}
        setFieldValue={formik.setFieldValue}
        error={getFormErrorMessage("firstContact")}
        name="firstContact"
        type="calendar"
        calendarProps={{
          dateFormat: displayedDateFormat,
          showIcon: true,
          inputStyle: { textTransform: "capitalize" },
          placeholder: dayjs().format(displayedDateFormat),
          maxDate: dayjs().toDate(),
        }}
        isRequiredInput
      />
      {/* EMAIL */}
      <FormikInput
        label={word("global.form.label.email")}
        flex
        isInvalid={isFormFieldInValid("email")}
        setFieldValue={formik.setFieldValue}
        getFieldProps={formik.getFieldProps}
        name="email"
        error={getFormErrorMessage("email")}
        placeHolder={word("auth.form.placeholder.email")}
      />
      {/* PHONE */}
      <FormikInput
        label={word("global.form.label.phoneNumber")}
        isInvalid={isFormFieldInValid("phoneNumberInput")}
        getFieldProps={formik.getFieldProps}
        name="phoneNumberInput"
        error={getFormErrorMessage("phoneNumberInput")}
        type="phoneNumber"
        setFieldValue={formik.setFieldValue}
        placeHolder={word("global.form.placeholder.phoneNumber")}
        phoneNumberProps={{
          phoneNumber: formik.getFieldProps("phoneNumberInput").value,
          countryCode: formik.getFieldProps("phoneNumberCountryCode").value,
          onPhoneNumberBlur: (e) => {
            if (formik.handleBlur) formik.handleBlur(e, "phoneNumberInput")
          },
          onPhoneNumberChange: (e) => {
            if (formik.handleChange) formik.handleChange(e, "phoneNumberInput")
          },
          onPhoneCodeChange: (e) => {
            formik.setFieldValue("phoneNumberCountryCode", e.target.value)
          },
        }}
      />
      {/* HEAR ABOUT US */}
      <div className="flex flex-column w-full">
        <FormikInput
          label={word("box.lead.form.input.hearAboutUs.label")}
          flex
          formikStyle={{ flexGrow: 1 }}
          isInvalid={isFormFieldInValid("hearAboutUs")}
          getFieldProps={formik.getFieldProps}
          name="hearAboutUs"
          error={getFormErrorMessage("hearAboutUs")}
          type="dropdown"
          placeHolder={word("box.lead.form.input.hearAboutUs.placeholder")}
          dropdownProps={{
            value: formik.values.hearAboutUs,
            options: hearAboutUsItem,
            onChange: (e) => {
              if (e.target.value) {
                formik.setFieldValue("hearAboutUsSpecify", undefined)
              }
            },
          }}
          setFieldValue={formik.setFieldValue}
        />
        {formik.values.hearAboutUs === "OTHER" && (
          <FormikInput
            flex
            formikStyle={{ flexGrow: 1 }}
            isInvalid={isFormFieldInValid("hearAboutUsSpecify")}
            getFieldProps={formik.getFieldProps}
            name="hearAboutUsSpecify"
            error={getFormErrorMessage("hearAboutUsSpecify")}
            placeHolder={word("box.lead.form.input.hearAboutUs.otherPlaceholder")}
          />
        )}
      </div>
      {/* EXPERIENCE LEVEL */}
      <FormikInput
        label={word("box.lead.form.input.experienceLevel.label")}
        flex
        formikStyle={{ flexGrow: 1 }}
        type="dropdown"
        name="experienceLevel"
        getFieldProps={formik.getFieldProps}
        isInvalid={isFormFieldInValid("experienceLevel")}
        error={getFormErrorMessage("experienceLevel")}
        dropdownProps={{
          value: formik.values.experienceLevel,
          options: experienceLevelItem,
        }}
        setFieldValue={formik.setFieldValue}
        placeHolder={word("box.lead.form.input.experienceLevel.placeholder")}
      />
      {/* GOALS */}
      <FormikInput
        label={word("box.lead.form.input.goals.label")}
        formikStyle={{ flexGrow: 1 }}
        type="multiselect"
        name="goals"
        getFieldProps={formik.getFieldProps}
        isInvalid={isFormFieldInValid("goals")}
        error={getFormErrorMessage("goals")}
        multiSelectProps={{
          options: goalsItem.map((goal) => {
            return {
              label: goal.label,
              value: goal.value,
            }
          }),
          values: formik.values.goals ?? [],
          displayTotalSelectedOptions: false,
          hideIcon: true,
          viewMore: word("box.lead.form.input.goals.showMore"),
        }}
        setFieldValue={formik.setFieldValue}
      />
      {/* BEST TIME TO CALL */}
      <FormikInput
        label={word("box.lead.form.input.bestTimeToContact.label")}
        placeHolder={word("box.lead.form.input.bestTimeToContact.placeholder")}
        getFieldProps={formik.getFieldProps}
        isInvalid={isFormFieldInValid("bestTimeToContact")}
        name="bestTimeToContact"
        error={getFormErrorMessage("bestTimeToContact")}
        type="textarea"
        formikStyle={{ flexGrow: 1 }}
        setFieldValue={formik.setFieldValue}
      />
      {/* INTERNAL NOTE */}
      <div className="flex flex-column gap-2">
        <HUText fontStyle="BL" color={theme.mainWhite}>
          {word("global.form.label.internalNote")}
        </HUText>
        <div className="flex flex-row gap-2 justify-content-center align-items-center">
          <WarningIcon color={theme.neutralColor900} width={30} height={30} />
          <HUText fontStyle="BS" color={theme.neutralColor400}>
            {word("box.lead.form.input.internalNote.warning")}
          </HUText>
        </div>
        <FormikInput
          flex
          formikStyle={{ flexGrow: 1, height: "8rem" }}
          type="textarea"
          placeHolder={word("box.lead.form.input.internalNote.placeholder")}
          getFieldProps={formik.getFieldProps}
          isInvalid={isFormFieldInValid("note")}
          name="note"
          error={getFormErrorMessage("note")}
          setFieldValue={formik.setFieldValue}
        />
      </div>
      {/* BUTTONS */}
      <div className=" mb-4 flex flex-row gap-2 align-items-center justify-content-center">
        <HUButton
          type="Left_Icon"
          size="M"
          colorType="Quaternary"
          text={word("global.previous")}
          onClick={onPrevious}
          className="w-full"
        />
        <HUButton
          type="Right_Icon"
          size="M"
          colorType="Primary"
          text={isEdition ? word("global.edit") : word("global.create")}
          onClick={handlePreSubmit}
          disabled={disabled || isSubmitting}
          submit
          className="w-full"
        />
      </div>
    </div>
  )
}
